import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, Grid, Typography, TextField, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';

import CheckboxOutlineBlank from '@material-ui/icons/CheckBoxOutlineBlank';
import Checkbox from '@material-ui/icons/CheckBox';
import InputWrapper from '../components/ui/InputWrapper';
import ErrorDialog from '../components/ui/ErrorDialog';

import LoaderIcon from '../components/ui/LoaderIcon';
import Warning from '../assets/images/figyelmezteto_tabla_lila.png';
import Info from '../assets/images/info_figyelmezteto_tabla_lila.png';

import useValidator from '../utils/useValidator';
import { isArrayNotEmpty, isBlank } from '../utils/Utils';
import { contractService } from '../services/contracting';
import { intraService } from '../services/intranet';
import { styles } from '../theme/styles';
import validatorRules from '../utils/validationRules';

const ContractingInitPage = props => {
  const [state, setState] = useState({
    roleError: false,
    type: 'R',
    year: '',
    officeEmail: '',
    employer: '',
    showError: false
  });

  const [dialog, setDialog] = useState({
    open: false,
    content: null,
    icon: '',
    disabled: false
  });

  const [emailSent, setEmailSent] = useState(false);

  const [loading, setLoading] = useState(true);
  const [errorMessages, setErrorMessages] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [pressedEnter, setPressedEnter] = useState(false);

  const { t } = useTranslation();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { classes } = props;
  const history = useHistory();

  const { giveErrors, hasErrors, setErrors, removeErrors } = useValidator();

  // Kötőjelet ellenőrző regexp
  const hyphenRegexp = /^([a-zA-Z0-9]*-[a-zA-Z0-9]*)+$/;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await contractService.init();
        console.log('response.data:', response.data);
        if (response.data?.status === 'OK') {
          // 2020-10-06 szbiro TNRUKAYCM-838 intranet folyamatban lévő státuszok kezelése
          if (response.data.token && !response.data.token.match(hyphenRegexp)) {
            if (response.data.type === 'U') {
              sessionStorage.setItem('type', '1');
            } else {
              sessionStorage.setItem('type', '0');
            }
            history.push(`/contracting/options/${response.data.token}`);
          }

          if (isArrayNotEmpty(response.data.messages)) {
            setErrorMessages(response.data.messages);
          } else if (response.data.type.length > 1) {
            const intraMessage = response.data.type.toLowerCase();
            if (intraMessage !== 'elutasitott') {
              setErrorMessages([`intra.status.${intraMessage}`]);
            }
          } else if (response.data.key && response.data.step) {
            sessionStorage.setItem('step', response.data.step);
            sessionStorage.setItem('contractKey', response.data.key);
            sessionStorage.setItem('type', response.data.type);
            history.push('/contracting');
          } else {
            setState({
              ...state,
              type: response.data.type,
              year: response.data.year
            });
            setErrorMessages([]);
          }
        } else {
          setErrorMessages(response.data?.messages);
        }
      } catch (e) {
        enqueueSnackbar(t('TECHNICAL_ERROR'), {
          variant: 'error'
        });
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const checkIfNeedsPopup = async () => {
      const response = await intraService.hasMessage();

      if (response.data.status === 'OK') {
        if (response.data.id !== 0) {
          const closeDialogContent = async () => {
            const resp = await intraService.closeMessage(response.data.id);

            if (resp.data.status === 'OK') {
              setDialog({
                open: false,
                content: null,
                icon: '',
                disabled: true
              });
            }
          };

          const dialogContent = (
            <Grid container>
              <Grid item xs={12}>
                <Typography
                  variant="subtitle1"
                  component="h6"
                  style={{
                    color: 'black',
                    fontWeight: '700',
                    fontSize: '14px',
                    marginTop: '24px'
                  }}>
                  {t('intra.contractDenied')}
                </Typography>
              </Grid>
              <Grid item xs={12} style={{ textAlign: 'right', marginBottom: '24px' }}>
                <Button variant="outlined" color="primary" onClick={closeDialogContent}>
                  {t('accept')}
                </Button>
                <Grid item xs={12}>
                  <Box className={props.classes.popupBottom} />
                </Grid>
              </Grid>
            </Grid>
          );

          setDialog({
            open: true,
            content: dialogContent,
            icon: 'warning',
            disabled: true
          });
        }
      }
    };

    checkIfNeedsPopup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (errorMessages.length) {
      setState({ ...state, roleError: true });
    } else {
      setState({ ...state, roleError: false });
    }
    setTimeout(() => setLoading(false), 600);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorMessages]);

  const checkUserEmail = async () => {
    if (!isBlank(state.officeEmail)) {
      try {
        const reg = !validatorRules.isRegKod(state.officeEmail);
        console.log('email', state.officeEmail, 'reg', reg);
        let emailResp;
        if (reg) {
          setState({ ...state, reg_kod: state.officeEmail });
        } else {
          emailResp = await contractService.checkEmail(state.officeEmail);
          if (emailResp.data.status !== 'OK') {
            emailResp.data.messages.map(message =>
              enqueueSnackbar(t(message), {
                variant: 'error'
              })
            );
            return;
          }
          if (emailResp.data.inUse) {
            setState({ ...state, employer: 0 });
            setEmployers([]);
            const key = enqueueSnackbar(t('emailInUse'), {
              variant: 'error'
              // persist: true,
            });
            giveErrors('officeEmail', key);
            return;
          }
        }
        const response = await contractService.listEmployersByDomain({
          type: state.type,
          email: state.officeEmail,
          code: reg ? state.officeEmail : null
        });
        if (response.data.status === 'OK') {
          setEmployers(response.data.list);
          if (!isArrayNotEmpty(response.data.list)) {
            setState({ ...state, employer: 0 });
            setEmployers([]);
            const key = enqueueSnackbar(t('EMAIL_INVALID'), {
              variant: 'error'
              // persist: true,
            });
            giveErrors('officeEmail', key);
          }
        } else {
          response.data.messages.map(message =>
            enqueueSnackbar(t(message), {
              variant: 'error'
            })
          );
        }
      } catch (e) {
        enqueueSnackbar(t('TECHNICAL_ERROR'), {
          variant: 'error'
        });
      }
    }
  };

  const handleBlur = (e, rules) => {
    if (e.target.name === 'officeEmail' && !pressedEnter) checkUserEmail();
    setErrors(e, rules);
  };

  const handleFocus = e => {
    removeErrors(e);
    setPressedEnter(false);
  };

  const handleOnChange = event => {
    const { name, value } = event.target;
    setState({ ...state, [name]: value });
    if (event.target.name === 'officeEmail') {
      setState({ ...state, [name]: value, employer: 0 });
      setEmployers([]);
    }
    removeErrors(event);
    setPressedEnter(false);
  };

  const handleEnter = (event, rules) => {
    const { charCode } = event;

    if (charCode === 13) {
      checkUserEmail();
      setPressedEnter(true);
      setErrors(event, rules);
    }
  };

  const handleSnackbar = snackbarKey => {
    closeSnackbar(snackbarKey);
    setEmailSent(false);
  };

  const handleSubmit = async event => {
    event.preventDefault();
    try {
      const { key } = employers.find(o => o.id === state.employer);
      const response = await contractService.start({
        type: state.type,
        employer: state.employer,
        email: state.officeEmail,
        key
      });

      if (response.data.status === 'OK') {
        setEmailSent(true);
        const action = snackbarKey => (
          <>
            <Button onClick={() => handleSnackbar(snackbarKey)}>OK</Button>
          </>
        );

        enqueueSnackbar(t('privateEmailSent'), {
          variant: 'success',
          // persist: true,
          action
        });
        setState({
          ...state,
          officeEmail: '',
          employer: 0
        });
      } else {
        response.data.messages.map(message =>
          enqueueSnackbar(t(message), {
            variant: 'error'
          })
        );
      }
    } catch (error) {
      enqueueSnackbar(error.message, {
        variant: 'error'
      });
    }
  };

  const basic = () => {
    const filtered = employers.filter(employer => employer.key === 0);
    return filtered.map(employer => (
      <FormControlLabel
        key={employer.id}
        value={employer.id}
        control={<Radio color="primary" icon={<CheckboxOutlineBlank />} checkedIcon={<Checkbox />} />}
        label={employer.name}
        style={{ marginLeft: '0' }}
      />
    ));
  };

  const intra = () => {
    const filtered = employers.filter(employer => employer.key === 1);
    return filtered.map(employer => (
      <FormControlLabel
        key={employer.id}
        value={employer.id}
        control={<Radio color="primary" icon={<CheckboxOutlineBlank />} checkedIcon={<Checkbox />} />}
        label={employer.name}
        style={{ marginLeft: '0' }}
      />
    ));
  };

  return (
    <Typography component="section" style={{ padding: '16px 32px 0px 32px' }}>
      {loading ? (
        <Grid container justify="flex-start">
          <Grid item xs={12}>
            <h2 style={{ fontSize: '14px', marginBottom: '0px' }}>{t('contractingHeading')}</h2>
          </Grid>
          <LoaderIcon />
        </Grid>
      ) : (
        <>
          <ErrorDialog
            showError={dialog.open}
            icon={dialog.icon}
            disabled={dialog.disabled}
            content={dialog.content}
            handleClose={() => {
              setDialog({ open: false, content: '', title: '' });
            }}
          />
          <form onSubmit={handleSubmit} onError={errors => console.error(errors)}>
            <Grid container justify="center">
              <Grid item xs={12}>
                <h2 style={{ fontSize: '14px', marginBottom: '0px' }}>{t('contractingHeading')}</h2>
              </Grid>
              <Grid item xs={12}>
                <Box display={state.roleError ? 'block' : 'none'}>
                  {errorMessages.map(message => (
                    <Grid item xs={12} key={message}>
                      <Grid container className={classes.warningField} alignItems="center" justify="flex-start">
                        <Grid item xs={12} sm="auto" className="warningSign">
                          <img src={Warning} alt="" />
                        </Grid>
                        <Grid item xs={12} sm className="warningText">
                          <p>{t(message)}</p>
                        </Grid>
                      </Grid>
                    </Grid>
                  ))}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box display={state.roleError ? 'none' : 'block'}>
                  <Grid container justify="center" alignItems="center" className={classes.warningField}>
                    <Grid item xs={12} sm="auto" style={{ textAlign: 'center' }}>
                      <img src={Info} style={{ width: '75px ', height: 'auto' }} alt="" />
                    </Grid>
                    <Grid item xs={12} sm className="warningText">
                      <p>
                        {t('contracting.subtitle')} {t('contracting.subtitle2')}
                      </p>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box style={{ marginTop: '-12px' }} width="100%" display={state.roleError ? 'none' : 'block'}>
                  <InputWrapper inputStyle={hasErrors('officeEmail') ? 'Error' : 'Main'} label={t('officeEmail')}>
                    <TextField
                      fullWidth
                      id="officeEmail"
                      name="officeEmail"
                      variant="outlined"
                      margin="dense"
                      value={state.officeEmail}
                      onChange={handleOnChange}
                      onBlur={e => handleBlur(e, ['required', 'isEmailOrRegKod'])}
                      onKeyPress={e => handleEnter(e, ['required', 'isEmailOrRegKod'])}
                      onFocus={handleFocus}
                      disabled={emailSent}
                    />
                  </InputWrapper>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box mx="auto" width="100%" display={state.roleError || (state.employer === 0 && !state.officeEmail) ? 'none' : 'block'}>
                  <FormControl fullWidth component="fieldset" style={{ marginTop: '20px' }}>
                    <FormLabel
                      component="legend"
                      style={{
                        fontWeight: '700',
                        fontSize: '14px',
                        color: 'black',
                        marginBottom: '16px'
                      }}>
                      {employers.length ? t('employer') : ''}
                    </FormLabel>
                    <RadioGroup
                      aria-label={t('paymentType')}
                      id="employer"
                      name="employer"
                      onChange={handleOnChange}
                      value={state.employer}
                      disabled={emailSent}>
                      { /* 2026-01-15 pjung AYCMUK-6 Sorrend csere */
                      intra().length ? <p style={{ margin: '6px 0' }}>{t('companyOfferHeading')}</p> : null}
                      {intra()}
                      {basic().length ? <p style={{ margin: '6px 0' }}>{t('basicOfferHeading')}</p> : null}
                      {basic()}
                    </RadioGroup>
                  </FormControl>
                </Box>
              </Grid>
              <Grid
                item
                xs={12}
                style={{ textAlign: 'right' }}
                // className={classes.bottomContractButton}
              >
                <Box display={state.roleError ? 'none' : 'block'} mb={2} mt={2}>
                  <Button
                    type="submit"
                    variant="outlined"
                    color="primary"
                    disabled={!state.employer || state.employer === 0 || emailSent}
                    style={{ width: 'auto', textTransform: 'none' }}>
                    {t('button.contracting')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </>
      )}
    </Typography>
  );
};

ContractingInitPage.propTypes = {
  classes: PropTypes.shape()
};

ContractingInitPage.defaultProps = {
  classes: {}
};

export default withStyles(styles)(ContractingInitPage);
